import { FC } from 'react'
import { KudosDto } from '../../../types'
import { Card } from 'antd'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import PerIcon from '../Icon'
import { useTranslation } from 'react-i18next'
import { KudosCard } from '../KudosCard/KudosCard'
import './Carousel.less'
import { ApolloError } from '@apollo/client'
import { KUDOS_CAROUSEL_PAGE_SIZE } from '../../../graphql/kudos/util'
import { NewLoadingAndErrors } from '../LoadingAndErrors/NewLoadingAndErrors'

export interface KudosCarouselProps {
  employeeOptedOut: boolean
  kudos: KudosDto[]
  nameDisplayPreference: string
  loading?: boolean
  error: ApolloError | null
  currentPage: number
  totalPages: number
  onNextPage: () => void
  onPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  privateProfileView: boolean
}

export const KudosCarousel: FC<KudosCarouselProps> = ({
  employeeOptedOut,
  kudos,
  nameDisplayPreference,
  loading,
  error = undefined,
  currentPage,
  totalPages,
  onNextPage,
  onPreviousPage,
  hasNextPage,
  hasPreviousPage,
  privateProfileView,
}) => {
  const { t } = useTranslation()

  const renderKudosCards = () => {
    const emptySlots = KUDOS_CAROUSEL_PAGE_SIZE - kudos.length
    const emptyCards = Array.from({ length: emptySlots }, (_, index) => (
      <div key={`empty-${index}`} className="kudos-carousel__empty-slot" />
    ))

    return (
      <div className="kudos-carousel__cards">
        {kudos.map((kudosItem) => {
          return <KudosCard key={kudosItem.id} kudos={kudosItem} nameDisplayPreference={nameDisplayPreference} />
        })}
        {emptyCards}
      </div>
    )
  }

  const renderNavigation = () => {
    if (totalPages <= 1) return null

    return (
      <PerFlexRow justify="space-between" align="middle" width="fullWidth">
        {hasPreviousPage ? (
          <PerButtonCircle
            icon="chevronLeft_stroke"
            color="secondary"
            variant="pastel"
            onClick={onPreviousPage}
            tooltip={t('kudos.previousPage')}
          />
        ) : (
          <div style={{ width: '32px', height: '32px' }} />
        )}

        <PerText color="secondary" fontSize="s">
          {currentPage + 1} {t('kudos.of')} {totalPages}
        </PerText>

        {hasNextPage ? (
          <PerButtonCircle
            icon="chevronRight_stroke"
            color="secondary"
            variant="pastel"
            onClick={onNextPage}
            tooltip={t('kudos.nextPage')}
          />
        ) : (
          <div style={{ width: '32px', height: '32px' }} />
        )}
      </PerFlexRow>
    )
  }

  const shouldShowNavigation = () => {
    return totalPages > 1
  }

  const renderStateCard = (content: React.ReactNode, hasFixedHeight = true) => (
    <Card bordered={false} style={hasFixedHeight ? { height: '280px' } : undefined}>
      <PerFlexRow
        direction="column"
        justify="center"
        align="middle"
        width="fullWidth"
        className={hasFixedHeight ? 'fullHeight' : undefined}
      >
        {content}
      </PerFlexRow>
    </Card>
  )

  const renderLoadingState = () =>
    renderStateCard(
      <>
        <PerIcon name="loading_fill" size={32} color="primary" spin />
        <PerText color="secondary" align="center" mt="s">
          {t('kudos.loading')}
        </PerText>
      </>,
      true,
    )

  const renderErrorState = () => (
    <NewLoadingAndErrors
      backgroundCardHeight={280}
      isLoading={false}
      error={error!}
      hasNoData={false}
      showSpinner
      t={t}
      customErrorMessage={
        privateProfileView
          ? t('page.error.pageUnavailable.customMessage.kudosCarouselPrivate')
          : t('page.error.pageUnavailable.customMessage.kudosCarouselPublic')
      }
    />
  )

  const renderEmptyState = () =>
    renderStateCard(
      <PerText color="secondary" align="center">
        {t('kudos.noKudos')}
      </PerText>,
      false,
    )

  const renderOptOutState = () =>
    renderStateCard(
      <PerText color="secondary" align="center">
        {privateProfileView
          ? t('kudos.profileSection.optOut.privateOptedOutDescription')
          : t('kudos.profileSection.optOut.employeeOptedOutDescription')}
      </PerText>,
      false,
    )

  if (loading || error || employeeOptedOut || kudos.length === 0) {
    return <>{loading ? renderLoadingState() : error ? renderErrorState() : employeeOptedOut ? renderOptOutState() : renderEmptyState()}</>
  }

  return (
    <>
      <Card bordered={false} style={{ height: shouldShowNavigation() ? '280px' : '232px' }}>
        <PerFlexRow direction="column" justify="space-between" className="fullHeight">
          {renderKudosCards()}
          {shouldShowNavigation() && renderNavigation()}
        </PerFlexRow>
      </Card>
    </>
  )
}

export default KudosCarousel
